/**
 * useSpeechRecognition: STT 상태 및 Jigasi 참여자 속성 동기화 훅.
 * 원본: wehago-conference useWhisper3.js + STTWithoutWhisper.js 에서 리팩토링.
 *
 * 브라우저 SpeechRecognition 구현은 이전 회의 호환을 위해 남겨 두지만,
 * 현재 정책에서는 실행하지 않고 서버 Jigasi STT만 사용한다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { store } from '@/store';
import { captionActions } from '@/store/slices/captionSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import { translationActions } from '@/store/slices/translationSlice';
import apiManager from '@/api';
import { translateOpenAI } from '@/api/translateAPI';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import { SYSTEM_MSG, saveMessageToDB } from '@/conference/systemMessages';
import { SpeechOwnershipGuard } from '@/utils/speechOwnershipGuard';
import { audioLevelStore } from '@/stores/audioLevelStore';
import { v4 as uuidv4 } from 'uuid';

// ─── 타입 ──────────────────────────────────────────────────────────────────

interface UseSpeechRecognitionOptions {
  /** Jitsi room.sendMessage 호출 함수 (ConferenceManager에서 전달) */
  sendMessage: (message: string) => void;
  /** STT 활성 여부 (Redux caption.isEnabled) */
  enabled: boolean;
  /** 마이크 mute 상태 */
  audioMuted: boolean;
  /** 현재 선택된 마이크 장치 ID. Web SpeechRecognition 재바인딩 트리거로 사용 */
  audioInputDeviceId: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SpeechRecognitionInstance = any;

interface StopRecognitionOptions {
  cancelReason?: string;
  finalizePending?: boolean;
}

interface ResetRecognitionOptions {
  cancelReason?: string;
}

const BROWSER_SPEECH_RECOGNITION_ENABLED = false;

// ─── 커스텀 훅 ──────────────────────────────────────────────────────────────

export function useSpeechRecognition({
  sendMessage,
  enabled,
  audioMuted,
  audioInputDeviceId,
}: UseSpeechRecognitionOptions) {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const voiceNote = useAppSelector((s) => s.voiceNote);
  const isJigasiTranscriberJoined = useAppSelector((s) => s.caption.isTranscriberJoined);
  const isJigasiTranscriberDialPending = useAppSelector((s) => s.caption.isTranscriberDialPending);
  const shouldPauseForJigasi = isJigasiTranscriberJoined || isJigasiTranscriberDialPending;
  // Jigasi translation_language(수신 언어) 갱신을 위해 언어 변경 구독
  const translationLanguage = useAppSelector((s) => s.translation.language);
  const isJigasiReceiverEnabled = useAppSelector(
    (s) => s.translation.isCaptionEnabled || s.translation.isTranslateEnabled
  );

  const recognitionRef = useRef<SpeechRecognitionInstance>(null);
  const [isListening, setIsListening] = useState(false);
  const tabIdRef = useRef(uuidv4());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const enabledRef = useRef(BROWSER_SPEECH_RECOGNITION_ENABLED && enabled && !audioMuted);
  const cancelCounterRef = useRef(0);
  const currentVoiceChatIdRef = useRef<string | null>(null);
  const pendingTranscriptRef = useRef('');
  // 현재 발화(utterance)의 첫 인식 결과 시각 — 서버 STT 통계의 발화 시간 계산용 startAt
  const utteranceStartAtRef = useRef<number | null>(null);
  const handleFinalTranscriptRef = useRef<(text: string) => Promise<void> | void>(() => undefined);
  const ownershipGuardRef = useRef(new SpeechOwnershipGuard());

  // START_STT / END_STT 시스템 메시지 중복 방지 (최초에는 false → 첫 시작시 전송)
  // STT 활성화 여부 (마이크 on/off와 무관하게 기능 켜짐/꺼짐 상태만 추적)
  const prevSttEnabledRef = useRef(false);

  // 무한 루트 방지용 변수
  const rapidRestartCountRef = useRef(0);
  const lastStartTimeRef = useRef(0);

  // 최신 활성 값 추적
  const isJigasiSttActive = useCallback(() => {
    const state = store.getState();
    if (state.caption.isTranscriberJoined || state.caption.isTranscriberDialPending) return true;
    return Boolean(getConferenceManager()?.getConnector()?.isJigasiTranscriberActive());
  }, []);

  useEffect(() => {
    enabledRef.current = BROWSER_SPEECH_RECOGNITION_ENABLED
      && enabled
      && !audioMuted
      && !!local.jitsiId
      && !shouldPauseForJigasi
      && !isJigasiSttActive();
  }, [enabled, audioMuted, local.jitsiId, shouldPauseForJigasi, isJigasiSttActive]);

  useEffect(() => {
    let prevAudioLevels: Record<string, number> | null = null;
    let prevActiveSpeakers: ReturnType<typeof store.getState>['audioIndicator']['activeSpeakers'] | null = null;
    let prevDominantSpeakerId: string | null = null;
    let prevLocalKey = '';

    const observeAudioState = () => {
      const state = store.getState();
      const localState = state.local;
      const audioIndicator = state.audioIndicator;
      const currentAudioLevels = audioLevelStore.getSnapshot();
      const localKey = [
        localState.jitsiId,
        localState.userId,
        localState.wehagoId,
        String(localState.audioMuted),
        String(localState.hasAudioTrack),
      ].join('|');

      if (
        prevAudioLevels === currentAudioLevels
        && prevActiveSpeakers === audioIndicator.activeSpeakers
        && prevDominantSpeakerId === audioIndicator.dominantSpeakerId
        && prevLocalKey === localKey
      ) {
        return;
      }

      prevAudioLevels = currentAudioLevels;
      prevActiveSpeakers = audioIndicator.activeSpeakers;
      prevDominantSpeakerId = audioIndicator.dominantSpeakerId;
      prevLocalKey = localKey;

      ownershipGuardRef.current.observeAudioSnapshot({
        localIds: [localState.jitsiId, localState.userId, localState.wehagoId],
        audioLevels: currentAudioLevels,
        activeSpeakers: audioIndicator.activeSpeakers,
        dominantSpeakerId: audioIndicator.dominantSpeakerId,
        audioMuted: localState.audioMuted,
        hasAudioTrack: localState.hasAudioTrack,
      });
    };

    observeAudioState();
    const unsubRedux = store.subscribe(observeAudioState);
    const unsubAudioLevel = audioLevelStore.subscribe(observeAudioState);
    return () => {
      unsubRedux();
      unsubAudioLevel();
    };
  }, []);

  // ─── BroadcastChannel: 멀티탭 STT 충돌 방지 ────────────────────────────

  useEffect(() => {
    if (!BROWSER_SPEECH_RECOGNITION_ENABLED) return;

    const channel = new BroadcastChannel('speech-api-status');
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent) => {
      const { type, tabID: senderTabID } = event.data ?? {};
      if (senderTabID === tabIdRef.current) return;

      if (type === 'STT_ALREADY_RUNNING') {
        // 다른 탭에서 STT가 활성화됨 → 현재 탭 STT 종료
        console.log('[STT] 다른 탭에서 STT 활성화됨 → 현재 탭 종료');
        dispatch(captionActions.setEnabled(false));
        dispatch(voiceNoteActions.setEnabled(false));
        stopRecognition({ cancelReason: 'other-tab' });
      } else if (type === 'STT_JOIN' && enabledRef.current) {
        // 다른 탭이 입장했는데 내가 이미 STT 켜져있으면 알려줌
        channel.postMessage({ type: 'STT_ALREADY_RUNNING', tabID: tabIdRef.current });
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [dispatch]);

  // ─── SpeechRecognition 생성 ─────────────────────────────────────────────

  const createRecognition = useCallback((): SpeechRecognitionInstance | null => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any;
    const Ctor = win.SpeechRecognition ?? win.webkitSpeechRecognition;
    if (!Ctor) {
      console.warn('[STT] 이 브라우저는 SpeechRecognition을 지원하지 않습니다.');
      return null;
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = voiceNote.language;
    return rec;
  }, [voiceNote.language]);

  const saveCancelMessageToDB = useCallback((cancelReason: string | undefined, transcript: string) => {
    if (!currentVoiceChatIdRef.current) return;

    const pendingTranscript = transcript.trim();
    const cancelMessagePrefix = `(cancel-${cancelReason ?? 'unknown'})`;
    const cancelMessage = pendingTranscript ? `${cancelMessagePrefix} ${pendingTranscript}` : cancelMessagePrefix;
    saveMessageToDB(cancelMessage, 'cancel');
  }, []);

  const clearLocalCurrentText = useCallback(() => {
    const currentLocal = store.getState().local;
    dispatch(captionActions.clearCurrentText(currentLocal.userId || currentLocal.jitsiId || undefined));
  }, [dispatch]);

  const cancelSpeechCandidate = useCallback((cancelReason: string, transcript: string) => {
    saveCancelMessageToDB(cancelReason, transcript);
    pendingTranscriptRef.current = '';
    currentVoiceChatIdRef.current = uuidv4();
    utteranceStartAtRef.current = null;
    clearLocalCurrentText();
  }, [clearLocalCurrentText, saveCancelMessageToDB]);

  // ─── STT 시작 ──────────────────────────────────────────────────────────

  const startRecognition = useCallback(() => {
    // 무한 루프(잦은 재생성) 방지 로직
    const now = Date.now();
    if (now - lastStartTimeRef.current < 2000) { // 2초 이내 재시작
      rapidRestartCountRef.current++;
      if (rapidRestartCountRef.current > 10) {    // 10회 이상 반복 시 강제 종료
        console.error('[STT] 비정상적인 무한 재시작 감지 → STT를 강제 종료합니다.');
        stopRecognition({ cancelReason: 'rapid-restart' });
        dispatch(captionActions.setEnabled(false));
        dispatch(voiceNoteActions.setEnabled(false));
        return;
      }
    } else {
      rapidRestartCountRef.current = 0;
    }
    lastStartTimeRef.current = now;

    // 기존 인스턴스 정리
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }

    const rec = createRecognition();
    if (!rec) return;

    // 새 voiceChatId 할당
    currentVoiceChatIdRef.current = uuidv4();
    pendingTranscriptRef.current = '';
    utteranceStartAtRef.current = null;
    cancelCounterRef.current = 0;
    ownershipGuardRef.current.resetCandidate();

    rec.onresult = (e: { resultIndex: number; results: SpeechRecognitionResultList }) => {
      if (!enabledRef.current || isJigasiSttActive()) return;

      if (utteranceStartAtRef.current == null) {
        utteranceStartAtRef.current = Date.now();
      }

      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        const text = result[0].transcript.trim();
        if (!text) continue;

        if (result.isFinal) {
          cancelCounterRef.current = 0;
          const decision = ownershipGuardRef.current.decideFinal(text);
          if (decision.type === 'reject') {
            if (decision.shouldCancel) {
              cancelSpeechCandidate(decision.reason, text || pendingTranscriptRef.current);
            } else {
              pendingTranscriptRef.current = '';
              clearLocalCurrentText();
            }
            interim = '';
            continue;
          }
          pendingTranscriptRef.current = '';
          handleFinalTranscript(text);
          interim = '';
        } else {
          interim += text;
        }
      }

      // interim 텍스트 → 자막 UI에 실시간 표시
      if (interim) {
        const pendingTranscript = interim.trim();
        pendingTranscriptRef.current = pendingTranscript;
        const decision = ownershipGuardRef.current.decideInterim(pendingTranscript);
        if (decision.type === 'hold') {
          return;
        }
        if (decision.type === 'reject') {
          if (decision.shouldCancel) {
            cancelSpeechCandidate(decision.reason, pendingTranscript);
          } else {
            pendingTranscriptRef.current = '';
            clearLocalCurrentText();
          }
          return;
        }

        const currentLocal = store.getState().local;
        const speakerId = currentLocal.userId || currentLocal.jitsiId;
        if (!speakerId) return;
        dispatch(captionActions.setCurrentText({
          speakerId,
          speakerName: currentLocal.userName || currentLocal.nickname || speakerId,
          nickname: currentLocal.nickname || '',
          profileUrl: currentLocal.profileUrl || '',
          text: pendingTranscript,
        }));

        // 다른 참여자에게 interim 전송
        sendVoiceMessage(pendingTranscript, 'T'); // isProgress: T(진행 중)
      }
    };

    rec.onerror = (e: { error: string }) => {
      console.warn('[STT] SpeechRecognition error:', e.error);
      // 이미 다른 인스턴스로 교체됐으면 무시 (stale 콜백 방지)
      if (recognitionRef.current !== rec) return;
      if (e.error === 'no-speech') {
        cancelCounterRef.current++;
        // 원본: cancelCounter > 5이면 재시작
        if (cancelCounterRef.current > 5) {
          console.log('[STT] no-speech 반복 → 재시작');
          cancelCounterRef.current = 0;
          resetRecognition({ cancelReason: 'no-speech' });
        }
      } else if (e.error === 'not-allowed') {
        console.error('[STT] 접근 권한 없음(not-allowed) → STT 자동 종료');
        stopRecognition({ cancelReason: 'not-allowed' });
        dispatch(captionActions.setEnabled(false));
        dispatch(voiceNoteActions.setEnabled(false));
      } else if (e.error === 'aborted') {
        // abort()에 의한 의도적 종료 — 재시작하지 않음 (effect cleanup/startRecognition에서 호출)
        console.log('[STT] aborted → 의도적 종료, 재시작 안 함');
      } else if (e.error === 'network') {
        // 네트워크 오류 → 재시작 시도
        if (enabledRef.current && recognitionRef.current === rec) {
          setTimeout(() => {
            if (enabledRef.current && recognitionRef.current === rec) {
              resetRecognition({ cancelReason: 'network' });
            }
          }, 500);
        }
      } else {
        // 기타 오류 발생 시
        console.warn('[STT] 기타 오류 발생 -> 재시작 보류');
      }
    };

    rec.onend = () => {
      // continuous 모드에서도 onend가 호출될 수 있음 (Chrome 특성)
      // enabled 상태면 자동 재시작
      if (enabledRef.current) {
        console.log('[STT] onend → 자동 재시작');
        setTimeout(() => {
          if (enabledRef.current && recognitionRef.current === rec) {
            resetRecognition({ cancelReason: 'onend' });
          }
        }, 300);
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = rec;

    try {
      rec.start();
      setIsListening(true);
    } catch (err) {
      console.warn('[STT] start 실패:', err);
      setIsListening(false);
    }
  }, [createRecognition, dispatch, isJigasiSttActive]);

  // ─── STT 중지 ──────────────────────────────────────────────────────────

  const stopRecognition = useCallback((options: StopRecognitionOptions = {}) => {
    const pendingTranscript = pendingTranscriptRef.current.trim();

    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }
    setIsListening(false);

    if (options.finalizePending && pendingTranscript) {
      const decision = ownershipGuardRef.current.decideFinal(pendingTranscript);
      if (decision.type === 'reject') {
        if (decision.shouldCancel) {
          saveCancelMessageToDB(decision.reason, pendingTranscript);
        }
        pendingTranscriptRef.current = '';
        currentVoiceChatIdRef.current = null;
        ownershipGuardRef.current.resetCandidate();
        clearLocalCurrentText();
        return;
      }

      pendingTranscriptRef.current = '';
      Promise.resolve(handleFinalTranscriptRef.current(pendingTranscript)).finally(() => {
        if (!enabledRef.current) {
          currentVoiceChatIdRef.current = null;
        }
      });
      return;
    }

    pendingTranscriptRef.current = '';

    // 진행 중인 STT가 있으면 cancel 처리 → DB 적재 (노출 없음)
    if (currentVoiceChatIdRef.current) {
      saveCancelMessageToDB(options.cancelReason, pendingTranscript);
      currentVoiceChatIdRef.current = null;
    }

    ownershipGuardRef.current.resetCandidate();
    clearLocalCurrentText();
  }, [clearLocalCurrentText, saveCancelMessageToDB]);

  // ─── STT 재시작 (원본 resetRecognition) ─────────────────────────────────

  const resetRecognition = useCallback((options: ResetRecognitionOptions = {}) => {
    console.log('[STT] 객체 재생성, language:', voiceNote.language);
    stopRecognition({ cancelReason: options.cancelReason ?? 'restart' });
    setTimeout(() => {
      if (enabledRef.current) {
        startRecognition();
      }
    }, 300);
  }, [stopRecognition, startRecognition, voiceNote.language]);

  // ─── Jitsi 메시지 전송 (원본 sendReduxVoiceMessage → room.sendTextMessage) ─

  const sendVoiceMessage = useCallback((text: string, isProgress: string, voiceChatId = currentVoiceChatIdRef.current) => {
    if (isJigasiSttActive()) return;

    const payload = {
      text,
      type: 'voice',
      voiceChatId,
      isProgress,
    };
    try {
      sendMessage(JSON.stringify(payload));
    } catch (err) {
      console.warn('[STT] sendMessage 실패:', err);
    }
  }, [sendMessage, isJigasiSttActive]);

  // ─── DB 적재 (원본 saveChatMessage) ─────────────────────────────────────

  const saveSpeechToDB = useCallback(async (message: string) => {
    if (isJigasiSttActive()) return;

    // store.getState()로 직접 읽어 stale closure 방지 (roomId, jitsiId는 회의 입장 후 세팅)
    const cur = store.getState().local;
    const utteranceStartAt = utteranceStartAtRef.current;
    utteranceStartAtRef.current = null;
    const data: Record<string, unknown> = {
      roomId: cur.roomId,
      userNo: cur.userNo || cur.userId,
      jitsiId: cur.jitsiId,
      userName: cur.userName,
      portalId: cur.wehagoId || cur.userId,
      employeeNo: cur.companyNo || '',
      profileUrl: cur.profileUrl,
      message,
      nickname: cur.nickname,
      type: 'voice',
      startAt: new Date(utteranceStartAt ?? Date.now()).toISOString(),
    };

    try {
      if (cur.isGuest) {
        await apiManager.saveChattingMessage2(data);
      } else {
        await apiManager.saveChattingMessage(data);
      }
    } catch (err) {
      console.warn('[STT] 채팅 저장 실패:', err);
    }
  }, [isJigasiSttActive]);

  // ─── 최종 텍스트 처리 ───────────────────────────────────────────────────

  const handleFinalTranscript = useCallback(async (text: string) => {
    if (isJigasiSttActive()) {
      pendingTranscriptRef.current = '';
      currentVoiceChatIdRef.current = null;
      ownershipGuardRef.current.resetCandidate();
      clearLocalCurrentText();
      return;
    }

    const voiceChatId = currentVoiceChatIdRef.current ?? uuidv4();
    pendingTranscriptRef.current = '';
    const currentLocal = store.getState().local;
    const speakerId = currentLocal.userId || currentLocal.jitsiId;
    if (!speakerId) return;
    const translationState = store.getState().translation;
    const { translatingUsers } = translationState;

    // 1) 자막 UI에 확정 텍스트 추가
    dispatch(captionActions.clearCurrentText(speakerId || undefined));
    dispatch(captionActions.addEntry({
      id: `stt-${voiceChatId}-${Date.now()}`,
      userId: speakerId,
      userName: currentLocal.userName || currentLocal.nickname || '나',
      nickname: currentLocal.nickname || '',
      profileUrl: currentLocal.profileUrl || '',
      text,
      nationality: voiceNote.language || 'ko',
      timestamp: Date.now(),
      isFinal: true,
    }));

    // 2) 번역 필요 여부 판단: translatingUsers 중 enabled인 참여자가 1명 이상
    const activeTranslators = Object.values(translatingUsers).filter((u) => u.enabled);
    const needTranslation = activeTranslators.length > 0;

    if (needTranslation) {
      // 발화자의 언어 (STT 설정 언어)
      const fromLang = voiceNote.language || 'ko';

      // 번역이 필요한 고유 대상 언어 목록 (자신의 언어와 동일하면 제외)
      const uniqueLangs = [...new Set(
        activeTranslators
          .map((u) => u.language)
          .filter((lang) => lang !== fromLang)
      )];

      if (uniqueLangs.length > 0) {
        // 병렬 번역 (wehago-conference VoiceChat.js getTranslate 패턴)
        try {
          const creditMode = currentLocal.creditMode;

          const results = await Promise.all(
            uniqueLangs.map(async (toLang) => {
              const res = await translateOpenAI({
                input: text,
                fromLang,
                toLang,
                consumptionType: creditMode,
                roomId: creditMode === 'MASTER' ? currentLocal.roomId : undefined,
              });

              // MASTER 모드: 서버 크레딧 부족 시 resultMsg로 알림
              if (res.resultMsg && res.resultMsg !== 'success') {
                console.warn('[STT] 번역 서버 응답:', res.resultMsg);
                // 크레딧 부족 시 번역 자동 OFF
                if (res.resultMsg === '크레딧이 부족합니다.') {
                  dispatch(translationActions.setTotalCredit(0));
                  dispatch(translationActions.setTranslateEnabled(false));
                }
                return { lang: toLang, translated: text };
              }

              return { lang: toLang, translated: res.resultData?.translatedText || text };
            })
          );

          // 다국어 JSON 조립: { ko: "원문", en: "translation", origin: "ko" }
          const multiLangText: Record<string, string> = { [fromLang]: text, origin: fromLang };
          for (const r of results) {
            multiLangText[r.lang] = r.translated;
          }

          // creditMode: MASTER이면 서버 즉시 차감 완료 / USER면 차감 없음
          const payload = {
            text: JSON.stringify(multiLangText),
            type: 'voice',
            voiceChatId,
            isProgress: 'F',
            isTranslated: true,
            creditMode,
          };
          if (isJigasiSttActive()) return;
          try {
            sendMessage(JSON.stringify(payload));
          } catch (err) {
            console.warn('[STT] sendMessage(translated) 실패:', err);
          }

          // 번역 완료 후 DB 적재 (번역본 포함 다국어 JSON)
          saveSpeechToDB(JSON.stringify(multiLangText));
        } catch (err) {
          console.warn('[STT] 번역 실패, 원문으로 전송:', err);
          // 번역 실패 시 원문 그대로 전송 + 원문으로 DB 적재
          sendVoiceMessage(text, 'F', voiceChatId);
          saveSpeechToDB(JSON.stringify({ [fromLang]: text, origin: fromLang }));
        }
      } else {
        // 대상 언어가 모두 발화자 언어와 동일 → 원문 전송 + 원문 DB 적재
        const fromLangForDB = voiceNote.language || 'ko';
        sendVoiceMessage(text, 'F', voiceChatId);
        saveSpeechToDB(JSON.stringify({ [fromLangForDB]: text, origin: fromLangForDB }));
      }
    } else {
      // 번역 불필요 → 원문 전송 + 원문 DB 적재 (STT 문장 완성 즉시)
      const fromLangForDB = voiceNote.language || 'ko';
      sendVoiceMessage(text, 'F', voiceChatId);
      saveSpeechToDB(JSON.stringify({ [fromLangForDB]: text, origin: fromLangForDB }));
    }

    // 4) 다음 발화를 위해 새 voiceChatId
    currentVoiceChatIdRef.current = uuidv4();
    ownershipGuardRef.current.resetCandidate();
  }, [clearLocalCurrentText, dispatch, isJigasiSttActive, sendVoiceMessage, saveSpeechToDB, voiceNote.language]);

  handleFinalTranscriptRef.current = handleFinalTranscript;

  // ─── enabled/audioMuted 변경에 따른 STT 시작/중지 ──────────────────────
  // jitsiId가 설정될 때까지(=회의 입장 완료) STT 시작을 지연시킨다.
  // 회의 연결 과정에서 jitsiId/audioMuted가 바뀔 때마다 effect가 재실행되어
  // abort → 재시작 연쇄가 발생하는 것을 방지한다.

  useEffect(() => {
    const isActive = enabled && !audioMuted && !!local.jitsiId;
    const mgr = getConferenceManager();
    const shouldUseJigasi = shouldPauseForJigasi || Boolean(mgr?.getConnector()?.isJigasiTranscriberActive());
    const shouldRunSpeechRecognition = BROWSER_SPEECH_RECOGNITION_ENABLED
      && isActive
      && !shouldUseJigasi;

    if (shouldRunSpeechRecognition) {
      startRecognition();
      // 다른 탭에 STT 활성화 알림
      channelRef.current?.postMessage({
        type: 'STT_ALREADY_RUNNING',
        tabID: tabIdRef.current,
      });
    } else {
      let cancelReason = 'inactive';
      if (shouldUseJigasi) {
        cancelReason = isJigasiTranscriberJoined || Boolean(mgr?.getConnector()?.isJigasiTranscriberActive())
          ? 'jigasi-mode'
          : isJigasiTranscriberDialPending
            ? 'jigasi-pending'
            : 'jigasi-active';
      } else if (!enabled) {
        cancelReason = 'disabled';
      } else if (audioMuted) {
        cancelReason = 'audio-muted';
      } else if (!local.jitsiId) {
        cancelReason = 'not-joined';
      }

      stopRecognition({
        cancelReason,
        finalizePending: enabled && audioMuted && !!local.jitsiId && !shouldUseJigasi,
      });
    }

    // Jigasi 모드: 참여자 속성(translation_language) 업데이트.
    // translation_language는 "내가 번역 결과를 받을 언어"(수신용)이므로
    // 마이크 mute 상태와 무관하게 STT/자막/번역 수신 여부에 연동한다.
    if (mgr && local.jitsiId) {
      mgr.getConnector()?.syncTranscriptionMode(enabled || isJigasiReceiverEnabled, translationLanguage);

      // 음성인식(STT) 활성화 여부에 따라 transcription_language (Jigasi Whisper 발화 언어) 설정
      const transcriptionLang = enabled ? (local.nationality || 'ko-KR') : 'NONE';
      mgr.getConnector()?.getRoom()?.setLocalParticipantProperty('transcription_language', transcriptionLang);
    }

    // 다른 참여자에게 STT ON/OFF 브로드캐스트
    if (mgr && local.jitsiId) {
      mgr.sendCommand(CONFERENCE_COMMANDS.CHANGE_SPEECH_BUTTON, {
        value: local.jitsiId,
        attributes: { isSpeechStart: String(shouldRunSpeechRecognition), reduxStartPerson: local.jitsiId },
      });

      // ── START_STT / END_STT 시스템 메시지 (상태가 변경된 경우에만) ──
      if (prevSttEnabledRef.current !== (enabled && !!local.jitsiId)) {
        const isFeatureActive = enabled && !!local.jitsiId;
        prevSttEnabledRef.current = isFeatureActive;

        const sttSysMsg = isFeatureActive ? SYSTEM_MSG.START_STT : SYSTEM_MSG.END_STT;
        const sttSysText = isFeatureActive
          ? `${local.userName || local.nickname}님의 발언이 기록됩니다.`
          : `${local.userName || local.nickname}님의 발언이 더이상 기록되지 않습니다.`;

          // 1) Jitsi 전송: 다른 사용자에게 system 메시지 전달
          try {
            mgr.sendChatMessage(JSON.stringify({
              type: 'system',
              message: sttSysMsg,
              text: sttSysText,
              senderName: local.userName || local.nickname,
            }));
          } catch (err) {
            console.warn('[STT] 시스템 메시지 전송 실패:', err);
          }

          // 2) 로컬 대화기록에 record_line 추가
          dispatch(captionActions.addEntry({
            id: `sys-${sttSysMsg}-${Date.now()}`,
            userId: local.jitsiId || local.userId,
            userName: local.userName || local.nickname,
            nickname: local.nickname || '',
            profileUrl: local.profileUrl || '',
            text: sttSysText,
            timestamp: Date.now(),
            isFinal: true,
            isSystem: true,
            systemMessage: sttSysMsg,
          }));

          // 3) DB 적재
          saveMessageToDB(sttSysMsg, 'system');
      }
    }

    return () => {
      // cleanup 시 중지
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* ignore */ }
        recognitionRef.current = null;
      }
    };
  }, [enabled, audioMuted, isJigasiReceiverEnabled, local.jitsiId, translationLanguage, shouldPauseForJigasi, isJigasiTranscriberJoined, isJigasiTranscriberDialPending]);

  // ─── 언어 변경 시 재시작 ────────────────────────────────────────────────

  useEffect(() => {
    if (!shouldPauseForJigasi && !isJigasiSttActive() && enabled && !audioMuted && isListening) {
      console.log('[STT] 언어 변경 → 재시작:', voiceNote.language);
      resetRecognition({ cancelReason: 'language-change' });
    }
  }, [voiceNote.language, shouldPauseForJigasi, isJigasiSttActive]);

  // Web SpeechRecognition은 특정 deviceId를 직접 지정할 수 없다.
  // 대신 선택 마이크가 바뀔 때 인스턴스를 재생성해 브라우저 입력 라우팅을 다시 잡게 한다.
  useEffect(() => {
    if (!shouldPauseForJigasi && !isJigasiSttActive() && enabled && !audioMuted && isListening) {
      console.log('[STT] 마이크 변경 → 재시작:', audioInputDeviceId || 'default');
      resetRecognition({ cancelReason: 'audio-input-change' });
    }
  }, [audioInputDeviceId, shouldPauseForJigasi, isJigasiSttActive]);

  // ─── 언마운트 시 리소스 정리 ─────────────────────────────────────────────

  useEffect(() => {
    return () => {
      console.log('[STT] 언마운트: 리소스 정리');
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* ignore */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  return { isListening };
}
